import React, { useEffect, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToastContext } from '../../../contexts/ToastContext';
import { useUserProfile, useUpdateUserProfile } from '../../../services/userService';

const AdminAccountSettings = () => {
  const { user, updateUser } = useAuth();
  const toast = useToastContext();
  const { data: profileData, isLoading } = useUserProfile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    const profile = profileData?.data;
    setFormData({
      name: profile?.name || user?.name || '',
      email: profile?.email || user?.email || '',
      phone: profile?.phone || user?.phone || '',
    });
  }, [profileData, user]);

  const updateMutation = useUpdateUserProfile({
    onSuccess: (data) => {
      if (data?.data) {
        updateUser({ ...user, ...data.data });
        setFormData((prev) => ({
          ...prev,
          name: data.data.name || prev.name,
          email: data.data.email || prev.email,
          phone: data.data.phone || prev.phone,
        }));
      }
      toast.success('Profile updated', 'Your account details were saved.');
    },
    onError: (error) => {
      toast.error('Update failed', error?.response?.data?.message || 'Could not update profile.');
    },
  });

  const handleSave = () => {
    if (!formData.name?.trim()) {
      toast.error('Validation', 'Name is required.');
      return;
    }
    updateMutation.mutate({
      name: formData.name.trim(),
      phone: formData.phone || null,
    });
  };

  if (isLoading) {
    return (
      <div className="py-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-xl">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-50 mb-1">
          Account details
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Update your name and phone. Email is tied to your login and cannot be changed here.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Full name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            disabled
            className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-500 !rounded-xl cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          />
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={updateMutation.isPending}
          className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2.5 !rounded-xl hover:bg-primary-700 text-sm font-medium disabled:opacity-50"
        >
          {updateMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save changes
        </button>
      </div>
    </div>
  );
};

export default AdminAccountSettings;
